import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type {AddressSearchResult} from '../../api/types';
import type {AddressSearchState, AddressSearchHandlers} from '../../hooks';
import {addressCaption, gnrBnrHint, keepServerResults} from '../../utils/address';

export interface AddressAutocompleteProps {
    searchState: AddressSearchState;
    searchHandlers: AddressSearchHandlers;
    /** Called when the selected address changes (after the hook is updated). */
    onSelect?: (address: AddressSearchResult | null) => void;
    label?: string;
    placeholder?: string;
    autoFocus?: boolean;
    fullWidth?: boolean;
}

const optionLabel = (opt: AddressSearchResult | string): string =>
    typeof opt === 'string'
        ? opt
        : opt.full_address || `${opt.adressenavn ?? ''} ${opt.husnummer ?? ''}${opt.bokstav ?? ''}`.trim();

/**
 * Shared address autocomplete — the canonical "Adressesøk" field used by both
 * the Adressesøk page and UDE project creation, so the two pickers look and
 * behave identically. freeSolo + full-address / gnr·bnr option rows, backed by
 * the `useAddressSearch` hook (passed in via searchState/searchHandlers). All
 * copy is Norwegian.
 */
export default function AddressAutocomplete({
    searchState,
    searchHandlers,
    onSelect,
    label = 'Adresse eller gnr/bnr',
    placeholder = 'En adresse («Storgata 1, Oslo»), eller gnr/bnr og kommune («101/23 Orkland»)',
    autoFocus = false,
    fullWidth = true,
}: AddressAutocompleteProps) {
    const {addressOptions, selectedAddress, searchQuery, searchLoading} = searchState;
    const {setSearchQuery, setSelectedAddress} = searchHandlers;

    return (
        <Autocomplete<AddressSearchResult, false, false, true>
            freeSolo
            fullWidth={fullWidth}
            options={addressOptions}
            filterOptions={keepServerResults}
            value={selectedAddress}
            inputValue={searchQuery || ''}
            loading={searchLoading}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(opt, val) =>
                typeof val !== 'string'
                && opt.gnr === val.gnr
                && opt.bnr === val.bnr
                && opt.kommune_code === val.kommune_code}
            onInputChange={(_, value) => setSearchQuery(value || '')}
            onChange={(_, value) => {
                const addr = value && typeof value !== 'string' ? value : null;
                setSelectedAddress(addr);
                onSelect?.(addr);
            }}
            renderOption={(props, option) => {
                const {key, ...rest} = props as typeof props & {key?: string};
                return (
                    <li
                        {...rest}
                        key={`${option.kommune_code}-${option.gnr}-${option.bnr}-${option.id}`}
                    >
                        <Box>
                            <Typography variant="body1">{option.full_address}</Typography>
                            <Typography variant="caption" sx={{
                                color: "text.secondary"
                            }}>
                                {addressCaption(option)}
                            </Typography>
                        </Box>
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus={autoFocus}
                    label={label}
                    placeholder={placeholder}
                    helperText={gnrBnrHint(searchQuery || '')}
                    slotProps={{
                        ...params.slotProps,

                        input: {
                            ...params.slotProps.input,
                            endAdornment: (
                                <>
                                    {searchLoading ? <CircularProgress color="inherit" size={18}/> : null}
                                    {params.slotProps.input.endAdornment}
                                </>
                            ),
                        }
                    }}
                />
            )}
        />
    );
}
